import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Globe2, ShieldCheck, Zap, Truck } from "lucide-react";
import { SITE, FACTORY_IMAGE, TEAM_IMAGE } from "@/lib/site";

export default function About() {
  return (
    <main data-testid="about-page" className="mx-auto max-w-7xl px-4 md:px-6 lg:px-8">
      <section className="pt-16 md:pt-24 pb-16 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-6">
          <div className="text-xs uppercase tracking-widest text-slate-500">About Green Fuse Energy</div>
          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tighter leading-[1.05] mt-3">
            A sourcing partner engineered for <span className="gfe-gradient-text">industrial reliability</span>.
          </h1>
          <p className="text-slate-600 mt-6 leading-relaxed text-base md:text-lg">
            Green Fuse Energy is a Rewari-based industrial supply company serving manufacturing, automation, food-processing, pharma and heavy-industry clients. We consolidate 17+ categories of MRO and OEM supply under a single trusted vendor — with quality assurance, competitive pricing and a partnership approach.
          </p>
          <div className="mt-8 grid grid-cols-2 gap-4">
            <div className="p-5 rounded-2xl border border-slate-200 bg-white">
              <div className="font-display text-3xl font-extrabold text-slate-900">80+</div>
              <div className="text-xs uppercase tracking-widest text-slate-500 mt-1">Brand Partners</div>
            </div>
            <div className="p-5 rounded-2xl border border-slate-200 bg-white">
              <div className="font-display text-3xl font-extrabold text-slate-900">17+</div>
              <div className="text-xs uppercase tracking-widest text-slate-500 mt-1">Product Categories</div>
            </div>
          </div>
        </div>
        <motion.div className="lg:col-span-6" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <div className="grid grid-cols-2 gap-4">
            <img src={FACTORY_IMAGE} alt="factory" className="rounded-2xl h-56 md:h-72 w-full object-cover" />
            <img src={TEAM_IMAGE} alt="team" className="rounded-2xl h-56 md:h-72 w-full object-cover mt-8" />
          </div>
        </motion.div>
      </section>

      <section className="py-16 border-t border-slate-200">
        <div className="grid md:grid-cols-2 gap-10 mb-12 items-end">
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-500">Our Promise</div>
            <h2 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight mt-2">Four commitments to every customer.</h2>
          </div>
          <p className="text-slate-600 md:justify-self-end max-w-md">
            The values that guide every quote we send and every consignment we ship.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {[
            { I: Globe2, t: "Global Sourcing Network", d: "Vetted relationships across Japan, Europe, US and India." },
            { I: ShieldCheck, t: "Quality Assured", d: "Full material & batch traceability. Genuine parts only." },
            { I: Zap, t: "Responsive Partnership", d: "24-hour quotes and urgent turnaround options." },
            { I: Truck, t: "Reliable Fulfilment", d: "Kitting, planning and multi-modal logistics." },
          ].map((v) => (
            <div key={v.t} className="p-6 rounded-2xl border border-slate-200 bg-white">
              <v.I className="w-6 h-6 text-slate-900" strokeWidth={1.5} />
              <h3 className="font-display text-lg font-bold mt-6">{v.t}</h3>
              <p className="text-sm text-slate-600 mt-2 leading-relaxed">{v.d}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="py-20">
        <div className="rounded-3xl bg-slate-50 border border-slate-200 p-8 md:p-14 grid md:grid-cols-2 gap-8 items-center">
          <div>
            <h2 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight">Let&apos;s build a shortlist for your plant.</h2>
            <p className="text-slate-600 mt-3">Share your specifications and we&apos;ll come back with pricing, lead time and 2–3 vetted alternatives.</p>
          </div>
          <div className="flex md:justify-end gap-3">
            <Link to="/quote" data-testid="about-cta-quote" className="btn-orange inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold">
              Request a Quote <ArrowRight className="w-4 h-4" />
            </Link>
            <a href={SITE.catalogue} target="_blank" rel="noreferrer" className="btn-ghost inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold">
              Get Catalogue
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}
